// One meaning per hue (DS §1.4, DC-18): every status word maps to one pill tone. The map is typed over the union of
// the status labels, so a status added to a chain in labels.ts does not compile until it has a tone here.

import type {
  ACKNOWLEDGMENT_STATUS, APPLICATION_STATUS, ASSESSMENT_STATUS, CANDIDATE_DECISION_STATUS, COMPENSATION_REVIEW_STATUS,
  DEBRIEF_READINESS_STATUS, DEBRIEF_STATUS, FINAL_LOOP_SCHEDULING_STATUS, HEALTH_BAND_LABELS, HM_REVIEW_STATUS,
  HM_SELECTION_STATUS, INTAKE_STATUS, INTERVIEW_STATUS, LEVEL_RULE_LABELS, OFFER_PACKAGE_STATUS, OFFER_STATUS,
  PACKET_STATUS, PANEL_CHANGE_STATUS, PROFILE_STATUS, RECOMMENDATION_LABELS, RECRUITER_ASSIGNMENT_STATUS,
  REFERENCE_STATUS, REQUEST_STATUS, REQUISITION_STATUS, SCHEDULING_STATUS, SCORECARD_STATUS, SCREEN_STATUS,
  SLA_PILL_LABELS, SLA_TIER_LABELS, SLATE_STATUS, SUMMARY_STATUS,
} from "./labels";

export type Tone = "ok" | "info" | "warn" | "flag" | "red" | "violet" | "neutral";

type Texts<T> = T[keyof T];

/** Every status word of G §5–§12, the SLA tiers and pill words, the health bands and Hire · No Hire. The playbook's
 *  other recommendation words take their tone from RECOMMENDATION_TONES (C5) through recoPillTone, not from here. */
export type ChainStatusWord =
  | Texts<typeof REQUEST_STATUS> | Texts<typeof REQUISITION_STATUS> | Texts<typeof RECRUITER_ASSIGNMENT_STATUS>
  | Texts<typeof COMPENSATION_REVIEW_STATUS> | Texts<typeof INTAKE_STATUS> | Texts<typeof ASSESSMENT_STATUS>
  | Texts<typeof PROFILE_STATUS> | Texts<typeof APPLICATION_STATUS> | Texts<typeof SLATE_STATUS>
  | Texts<typeof HM_REVIEW_STATUS> | Texts<typeof SCREEN_STATUS> | Texts<typeof HM_SELECTION_STATUS>
  | Texts<typeof CANDIDATE_DECISION_STATUS> | Texts<typeof SCHEDULING_STATUS> | Texts<typeof FINAL_LOOP_SCHEDULING_STATUS>
  | Texts<typeof PACKET_STATUS> | Texts<typeof ACKNOWLEDGMENT_STATUS> | Texts<typeof PANEL_CHANGE_STATUS>
  | Texts<typeof INTERVIEW_STATUS> | Texts<typeof SCORECARD_STATUS> | Texts<typeof DEBRIEF_READINESS_STATUS>
  | Texts<typeof SUMMARY_STATUS> | Texts<typeof DEBRIEF_STATUS> | (typeof RECOMMENDATION_LABELS)["hire" | "noHire"]
  | Texts<typeof REFERENCE_STATUS> | Texts<typeof OFFER_PACKAGE_STATUS> | Texts<typeof OFFER_STATUS>
  | Texts<typeof SLA_TIER_LABELS> | Texts<typeof SLA_PILL_LABELS> | Texts<typeof HEALTH_BAND_LABELS>
  | (typeof LEVEL_RULE_LABELS)["concernFlag"];

/** The further words DS §1.4 names for a hue; a group that draws one registers its label in its own module. */
export type DutyWord =
  | "Confirmed" | "Proposed" | "Suggested" | "Sync failure" | "Uncovered" | "Conflict" | "Internal"
  | "Panel change" | "Consulted" | "Material panel structure change" | "Limited";

export type StatusWord = ChainStatusWord | DutyWord;

const TONE: Readonly<Record<StatusWord, Tone>> = {
  // green: done, agreed, ready
  Approved: "ok", Accepted: "ok", Ready: "ok", "Ready for Verbal Offer": "ok", Finalized: "ok", Released: "ok",
  Scheduled: "ok", Submitted: "ok", Hire: "ok", Advance: "ok", Advanced: "ok", "Outcome Recorded": "ok", Green: "ok",
  Confirmed: "ok", "HM Certified": "ok", Created: "ok", Posted: "ok", Assigned: "ok", "Offer Accepted": "ok",
  "Recruiting complete": "ok", "Ready for HRIS": "ok", "HRIS Hired": "ok", Filled: "ok", Qualified: "ok",
  "Intake Scheduled": "ok", "Interview Scheduled": "ok", "Ready for Debrief": "ok", "Ready to Schedule": "ok",
  // blue: active, selected, in progress, information
  "In HM Review": "info", "In Review": "info", "In Progress": "info", Requested: "info", "Screen Requested": "info",
  "Scheduling in Progress": "info", Proposed: "info", Suggested: "info", Recommended: "info", Due: "info",
  "HM Interview Scheduling Requested": "info", "Draft Plan": "info", Sent: "info", Reviewed: "info", Generated: "info",
  Compiling: "info", "Recruiter Review": "info", "Verbal Extended": "info", "Written Sent": "info",
  Negotiation: "info", Active: "info", Reopened: "info",
  // gold: waiting, 50 % reminder, hold, attention without fault
  "Awaiting Candidate Availability": "warn", "Awaiting Interviewer Availability": "warn", "Awaiting Availability": "warn",
  "Awaiting Candidate": "warn", Reminder: "warn", "On Hold": "warn", Held: "warn", Hold: "warn", "Not Ready": "warn",
  Yellow: "warn", "Pending Recruiter Review": "warn", "Pending HM Review": "warn", Required: "warn",
  "Pending HM Certification": "warn", "Pending HRBP Approval": "warn", "Pending TA Manager": "warn",
  "Pending Approvals": "warn", "Req Creation Pending": "warn", "Cancellation Pending": "warn", "Reopen Pending": "warn",
  "Waiting for Inputs": "warn", "Scorecard Due": "warn", "Revised Offer": "warn",
  // orange: flag, near-breach, something to discuss
  "Near-breach": "flag", "Concern flag": "flag", Conflict: "flag", "Reschedule Requested": "flag", Returned: "flag",
  "Revision Required": "flag", "Unable to Complete": "flag",
  // red: breach, failure, refusal
  Overdue: "red", "Past Due": "red", "Breach — dashboard risk": "red", "Sync failure": "red", Blocked: "red",
  "Blocked for panel/coverage": "red", Declined: "red", Rejected: "red", "No Hire": "red", Uncovered: "red",
  "Validation Failed": "red", Red: "red",
  // purple: structure and role
  Internal: "violet", "Panel change": "violet", Consulted: "violet", "Material panel structure change": "violet",
  "Roll Call": "violet", Reveal: "violet", Discussion: "violet", Alignment: "violet",
  // neutral: not started, closed, recorded
  Draft: "neutral", Pending: "neutral", "Pending HM Selection": "neutral", "Pending Review": "neutral",
  "Acknowledgment Pending": "neutral", Completed: "neutral", Communicated: "neutral", "Not Required": "neutral",
  Pass: "neutral", Passed: "neutral", Withdrawn: "neutral", Dispositioned: "neutral", Cancelled: "neutral",
  Archived: "neutral", Dormant: "neutral", Waived: "neutral", Limited: "neutral", "Not Started": "neutral",
  "Draft Batch": "neutral", "Packet Draft": "neutral", Sourced: "neutral", Merged: "neutral",
};

/** The pill tone of a recommendation tone (C5): the strong form is green with weight (its own class), orange is a flag. */
export const RECO_PILL_TONE: Readonly<Record<"ok-strong" | "ok" | "concern" | "bad" | "wait", Tone>> = {
  "ok-strong": "ok", ok: "ok", concern: "flag", bad: "red", wait: "warn",
};

/** The pill tone a recommendation's tone wears (Pill tone; the strong form adds its own weight). */
export function recoPillTone(tone: keyof typeof RECO_PILL_TONE): Tone {
  return RECO_PILL_TONE[tone];
}

export function toneOf(word: StatusWord): Tone {
  return TONE[word];
}

export function isStatusWord(word: string): word is StatusWord {
  return Object.prototype.hasOwnProperty.call(TONE, word);
}

/** Every status word with a tone, for tests and the gallery's seven-tone row. */
export const STATUS_WORDS = Object.keys(TONE) as readonly StatusWord[];
